import { ReactNode, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, isPowerUser, getPdpoRoleName } from '../../contexts/AuthContext';
import { useCredits } from '../../hooks/useCredits';
import { Coins, Building2, Shield, LogOut, Sparkles, Bell, FileText, Users, HelpCircle, Beaker } from 'lucide-react';
import { getNotificationCounts, subscribeToNotifications } from '../../services/notificationService';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const { balance, loading: creditsLoading } = useCredits();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const menuRef = useRef<HTMLDivElement>(null);

  // Get PDPO role information
  const isPower = isPowerUser(userProfile);
  const roleName = getPdpoRoleName(userProfile);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUserMenu]);

  useEffect(() => {
    if (userProfile?.id) {
      loadNotificationCount();

      const unsubscribe = subscribeToNotifications(userProfile.id, () => {
        loadNotificationCount();
      });

      return () => {
        unsubscribe();
      };
    }
  }, [userProfile?.id]);

  const loadNotificationCount = async () => {
    if (!userProfile?.id) return;

    try {
      const counts = await getNotificationCounts(userProfile.id);
      setNotificationCount(counts.unread);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  // ============================================================================
  // BUILD NAVIGATION BASED ON PDPO ROLE
  // ============================================================================

  const navigation = [
    // ========================================================================
    // POWER USERS MENU (DPO, CEO, CFO, LEGAL, DEPT_HEAD, MANAGER)
    //  👉 This is now your "maximum" top-level sidebar list.
    // ========================================================================
    ...(isPower
      ? [
          {
            path: '/dashboard',
            name: 'Dashboard',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            ),
          },
          {
            path: '/compliance',
            name: 'Compliance Hub',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            ),
          },
          {
            path: '/actions',
            name: 'Action Tracker',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            ),
          },
          {
            path: '/data-register',
            name: 'Data Register',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            ),
          },
          {
            path: '/consent',
            name: 'Consent Management',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ),
          },
          {
            path: '/access-requests',
            name: 'Access Requests',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            ),
          },
          {
            path: '/breaches',
            name: 'Breach Logging',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            ),
          },
          {
            path: '/public-page',
            name: 'Public Trust Page',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 21a9 9 0 100-18 9 9 0 000 18zm0 0c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3 7.5 7.03 7.5 12s2.015 9 4.5 9zM3.6 9h16.8M3.6 15h16.8"
                />
              </svg>
            ),
          },
          {
            path: '/vendors',
            name: 'Vendors & Processors',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            ),
          },
        ]
      : []),

    // ========================================================================
    // STAFF MENU (Limited - "Light Version")
    // ========================================================================
    ...(!isPower
      ? [
          {
            path: '/actions',
            name: 'My Actions',
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            ),
          },
        ]
      : []),

    // ========================================================================
    // EVERYONE SEES THESE
    // ========================================================================
    {
      path: '/documents',
      name: 'Documents',
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      path: '/training',
      name: 'Training',
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332-.477 4.5-1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
  ];

  const handleSignOut = async () => {
    try {
      setShowUserMenu(false);
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleMenuNavigation = (path: string) => {
    setShowUserMenu(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ====================================================================== */}
      {/* HEADER */}
      {/* ====================================================================== */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-6">
              {/* Logo - Click to go to appropriate home */}
              <button
                onClick={() => navigate(isPower ? '/dashboard' : '/actions')}
                className="hover:opacity-80 transition-opacity"
              >
                <h1 className="text-xl font-bold text-slate-900">PDPO Assist</h1>
                <p className="text-xs text-slate-600">Compliance Platform</p>
              </button>

              {/* AI Adviser Button */}
              <button
                onClick={() => navigate('/ai-adviser')}
                className={`relative flex items-center space-x-2 px-5 py-2.5 rounded-lg font-semibold shadow-lg transition-all duration-300 overflow-hidden group ${
                  location.pathname === '/ai-adviser'
                    ? 'bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 text-white shadow-blue-500/50 scale-105'
                    : 'bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 text-white hover:shadow-xl hover:scale-105 shadow-blue-400/40'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 translate-x-[-100%] group-hover:translate-x-[100%] transition-all duration-1000"></div>
                <Sparkles className="w-5 h-5 relative z-10" />
                <span className="relative z-10 font-bold">DPO.ai</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 blur-xl transition-opacity"></div>
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {/* Try the new (workflow-oriented) interface */}
              <button
                onClick={() => navigate('/inbox?ui=new')}
                className="hidden md:flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition"
                title="Switch to the new workflow-oriented interface (preview)"
              >
                <Beaker className="w-3.5 h-3.5" />
                <span>Try new interface</span>
              </button>

              {/* Credits - Power users only */}
              {isPower && (
                <button
                  onClick={() => navigate('/credits')}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                  title="View credits and billing"
                >
                  <Coins className="w-5 h-5 text-slate-700" />
                  <div className="text-left">
                    <p className="text-xs text-slate-500 leading-none">Credits</p>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">
                      {creditsLoading ? '...' : balance.toLocaleString()}
                    </p>
                  </div>
                </button>
              )}

              {/* User Info */}
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">
                  {userProfile?.name}
                </p>
                <p className="text-xs text-slate-600">{roleName}</p>
              </div>

              {/* User Menu Dropdown */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="relative w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold hover:bg-blue-200 transition-all hover:scale-105"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  {userProfile?.name?.charAt(0).toUpperCase()}
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                    {/* Company Profile - Everyone */}
                    <button
                      onClick={() => handleMenuNavigation('/profile')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                    >
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="font-medium">Company Profile</span>
                    </button>

                    {/* PDPO Roles - Power users only */}
                    {isPower && (
                      <button
                        onClick={() => handleMenuNavigation('/authorities')}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                      >
                        <Shield className="w-4 h-4 text-slate-500" />
                        <span className="font-medium">PDPO Roles</span>
                      </button>
                    )}

                    <div className="my-1 border-t border-slate-200"></div>

                    {/* Notifications - Everyone */}
                    <button
                      onClick={() => handleMenuNavigation('/notifications')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                    >
                      <Bell className="w-4 h-4 text-slate-500" />
                      <div className="flex items-center justify-between flex-1">
                        <span className="font-medium">Notifications</span>
                        {notificationCount > 0 && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                            {notificationCount}
                          </span>
                        )}
                      </div>
                    </button>

                    {/* Audit Log - Power users only */}
                    {isPower && (
                      <button
                        onClick={() => handleMenuNavigation('/audit-log')}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                      >
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="font-medium">Audit Log</span>
                      </button>
                    )}

                    {/* User Management - Power users only */}
                    {isPower && (
                      <>
                        <div className="my-1 border-t border-slate-200"></div>
                        <button
                          onClick={() => handleMenuNavigation('/user-management')}
                          className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                        >
                          <Users className="w-4 h-4 text-slate-500" />
                          <span className="font-medium">User Management</span>
                        </button>
                      </>
                    )}

                    {/* Help - Everyone */}
                    <button
                      onClick={() => handleMenuNavigation('/help')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                    >
                      <HelpCircle className="w-4 h-4 text-slate-500" />
                      <span className="font-medium">Help</span>
                    </button>

                    <div className="my-1 border-t border-slate-200"></div>

                    {/* Sign Out - Everyone */}
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-3"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="font-medium">Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ====================================================================== */}
      {/* MAIN LAYOUT */}
      {/* ====================================================================== */}
      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-4rem)] sticky top-16">
          <nav className="p-4 space-y-1">
            {navigation.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition text-left ${
                  location.pathname === item.path
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex-shrink-0">{item.icon}</div>
                <span className="flex-1">{item.name}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}
